import { createStreamingAsyncGenerator } from '@/shared/lib/streaming'
import { ApiIndividualResponse, ApiPaginatedResponse } from '@/types/api'
import {
  ApiPlaybookCategory,
  ApiPlaybookCreationRequest,
  ApiPlaybookStepStatus,
  ApiPlaybookUpdateRequest,
  PlaybookFeedback,
  PlaybookFeedbackCreationPayload,
  PlaybookTrainingVideosPayload,
} from '@/types/playbook'
import { PlaybookMessageStreamEventUnion } from '@/types/playbook-message-stream'
import { PlaybookTestEventUnion } from '@/types/playbook-test-message-stream'
import { ApiService, RequestOptions } from './api'

interface PlaybooksFilters {
  [key: string]: string | number | boolean | undefined
}

export interface PlaybooksPagination {
  page?: number
  limit?: number
}

export type ListPlaybooksOptions = {
  filters?: PlaybooksFilters
  pagination?: PlaybooksPagination
  options?: RequestOptions
}

export class PlaybooksService {
  static readonly path = '/playbooks'
  static readonly playbookPath = '/playbooks/:id'
  static readonly messagesPath = '/playbooks/:id/messages'
  static readonly playbookAgentStatusPath = '/playbooks/:id/status'
  static readonly playbookStepPath = '/playbooks/:id/steps/:stepIndex'
  static readonly categoriesPath = '/playbooks/categories'
  static readonly testPromptPath = '/playbooks/:id/prompt-tests'
  static readonly trainingVideosPath = '/playbooks/:id/training-videos'
  static readonly feedbackPath = '/playbooks/:id/feedbacks'

  static readonly httpClient = new ApiService()

  static async list({
    filters = {},
    pagination = {},
    options,
  }: ListPlaybooksOptions = {}) {
    let url = PlaybooksService.path
    const queryParams = new URLSearchParams()

    if (pagination.limit) {
      queryParams.append('limit', pagination.limit.toString())
    }
    if (pagination.page) {
      queryParams.append('page', pagination.page.toString())
    }

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString())
      }
    })

    if (queryParams.toString()) {
      url = `${url}?${queryParams.toString()}`
    }

    return await PlaybooksService.httpClient.get(url, options)
  }

  static async getPlaybookById(id: string, options?: RequestOptions) {
    return await PlaybooksService.httpClient.get(
      PlaybooksService.playbookPath.replace(':id', id),
      options
    )
  }

  static async updatePlaybook(
    id: string,
    playbookData: ApiPlaybookUpdateRequest,
    options?: RequestOptions
  ) {
    return await PlaybooksService.httpClient.patch(
      PlaybooksService.playbookPath.replace(':id', id),
      playbookData,
      options
    )
  }

  static async getPlaybookAgentStatus(id: string, options?: RequestOptions) {
    return await PlaybooksService.httpClient.get(
      PlaybooksService.playbookAgentStatusPath.replace(':id', id),
      options
    )
  }

  static async getPlaybookMessages(id: string, options?: RequestOptions) {
    return await PlaybooksService.httpClient.get(
      PlaybooksService.messagesPath.replace(':id', id),
      options
    )
  }

  static async postPlaybookMessage(
    playbookId: string,
    content: string,
    options?: RequestOptions
  ) {
    return await PlaybooksService.httpClient.post(
      PlaybooksService.messagesPath.replace(':id', playbookId),
      { message: content },
      options
    )
  }

  static async *streamPlaybookMessage(
    playbookId: string,
    content: string,
    options?: RequestOptions & { signal?: AbortSignal }
  ): AsyncGenerator<PlaybookMessageStreamEventUnion, void, unknown> {
    const url =
      PlaybooksService.httpClient.API_BASE_URL +
      PlaybooksService.messagesPath.replace(':id', playbookId)

    yield* createStreamingAsyncGenerator<PlaybookMessageStreamEventUnion>({
      url,
      body: { message: content },
      headers: (options?.headers as Record<string, string>) || {},
      signal: options?.signal,
      onMessage: (event) => {
        try {
          const data = JSON.parse(event.data)

          switch (event.event) {
            case 'delta':
              if (data.text !== undefined) {
                return { type: 'delta', message: data.text }
              }
              break
            case 'processing':
              if (data.message !== undefined) {
                return { type: 'processing', message: data.message }
              }
              break
            case 'title_generated':
              if (data.message !== undefined) {
                return { type: 'title_generated', message: data.message }
              }
              break
            case 'agent_update':
              if (data.message !== undefined) {
                return { type: 'agent_update', message: data.message }
              }
              break
            case 'error':
              if (data.message !== undefined) {
                return { type: 'error', message: data.message }
              }
              break
            case 'done':
              return {
                type: 'done',
                meta: {
                  playbookUpdated: data.playbookUpdated ?? false,
                },
              }
          }
        } catch {
          // Ignore parse errors
        }
        return null
      },
      onError: (error) => ({
        type: 'error',
        message: error.message,
      }),
    })
  }

  static async createPlaybook(
    playbookData: ApiPlaybookCreationRequest,
    options?: RequestOptions
  ) {
    return await PlaybooksService.httpClient.post(
      PlaybooksService.path,
      playbookData,
      options
    )
  }

  static async duplicatePlaybook(
    fromPlaybookId: string,
    options?: RequestOptions
  ) {
    if (!fromPlaybookId) {
      throw new Error('fromPlaybookId is required')
    }

    return await PlaybooksService.httpClient.post(
      PlaybooksService.path,
      { fromPlaybookId },
      options
    )
  }

  static async deletePlaybook(
    playbookId: string,
    options?: RequestOptions
  ): Promise<void> {
    if (!playbookId) {
      throw new Error('playbookId is required')
    }

    return await PlaybooksService.httpClient.delete(
      PlaybooksService.playbookPath.replace(':id', playbookId),
      options
    )
  }

  static async updateStepStatus(
    playbookId: string,
    stepIndex: number,
    stepStatus: ApiPlaybookStepStatus,
    options?: RequestOptions
  ) {
    if (!playbookId || !(stepIndex >= 0) || !stepStatus) {
      throw new Error('playbookId, stepIndex, and stepStatus are required')
    }

    return await PlaybooksService.httpClient.patch(
      PlaybooksService.playbookStepPath
        .replace(':id', playbookId)
        .replace(':stepIndex', stepIndex.toString()),
      {
        status: stepStatus,
      },
      options
    )
  }

  static async getPlaybookCategories(
    options?: RequestOptions
  ): Promise<ApiIndividualResponse<ApiPlaybookCategory[]>> {
    return await PlaybooksService.httpClient.get(
      PlaybooksService.categoriesPath,
      options
    )
  }

  static async *testPrompt(
    playbookId: string,
    prompt: string,
    options?: RequestOptions & { signal?: AbortSignal }
  ): AsyncGenerator<PlaybookTestEventUnion, void, unknown> {
    const url =
      PlaybooksService.httpClient.API_BASE_URL +
      PlaybooksService.testPromptPath.replace(':id', playbookId)

    yield* createStreamingAsyncGenerator<PlaybookTestEventUnion>({
      url,
      body: { prompt },
      headers: (options?.headers as Record<string, string>) || {},
      signal: options?.signal,
      onMessage: (event) => {
        try {
          const data = JSON.parse(event.data)

          switch (event.event) {
            case 'delta':
              if (data.text !== undefined) {
                return { type: 'delta', text: data.text }
              }
              break
            case 'error':
              if (data.message !== undefined) {
                return { type: 'error', message: data.message }
              }
              break
            case 'done':
              return { type: 'done' }
          }
        } catch {
          // Ignore parse errors
        }
        return null
      },
      onError: (error) => ({
        type: 'error',
        message: error.message,
      }),
    })
  }

  static async getPlaybookTrainingVideos(
    playbookId: string,
    options?: RequestOptions
  ): Promise<PlaybookTrainingVideosPayload> {
    return await PlaybooksService.httpClient.get(
      PlaybooksService.trainingVideosPath.replace(':id', playbookId),
      options
    )
  }

  static async getPlaybookFeedbacks(
    id: string,
    playbookVersionId?: string,
    options?: RequestOptions
  ): Promise<ApiPaginatedResponse<PlaybookFeedback>> {
    const urlSearchParams = new URLSearchParams()

    if (playbookVersionId) {
      urlSearchParams.set('playbookVersionId', playbookVersionId)
    }

    const basePath = PlaybooksService.feedbackPath.replace(':id', id)

    return await PlaybooksService.httpClient.get(
      `${basePath}?${urlSearchParams}`,
      options
    )
  }

  static async createPlaybookFeedback(
    id: string,
    payload: PlaybookFeedbackCreationPayload,
    options?: RequestOptions
  ): Promise<ApiIndividualResponse<PlaybookFeedback>> {
    return await PlaybooksService.httpClient.post(
      PlaybooksService.feedbackPath.replace(':id', id),
      payload,
      options
    )
  }
}
